'use client';

import {
  ReadonlyURLSearchParams,
  useRouter,
  useSearchParams,
} from 'next/navigation';
import { FC, ReactNode, useMemo, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Box } from '@virginmediao2/momentum-nextjs/box';
import { Button } from '@virginmediao2/momentum-nextjs/button';
import {
  ErrorAlert,
  ErrorAlertsRef,
  FormCheckboxGroup,
  FormCheckboxInput,
  FormInput,
  FormRadioGroup,
  FormRadioInput,
} from '@virginmediao2/momentum-nextjs/forms';
import { Text } from '@virginmediao2/momentum-nextjs/text';
import { VerticalRhythm } from '@virginmediao2/momentum-nextjs/vertical-rhythm';

import { formSubmit } from './form-submit';
import { BRANDS, CUSTOMER_TYPES, Option, PRODUCTS_SERVICES } from './options';
import {
  FORM_FIELD_ORDER,
  GetReleaseUpdatesForm,
  getReleaseUpdatesSchema,
} from './schema';

export interface GetReleaseUpdatesProps {
  renderPreSubmit: ReactNode;
  successUrl: string;
}

const getInitialParamValues = <T extends readonly Option<string>[]>(
  paramName: 'products' | 'brands',
  searchParams: ReadonlyURLSearchParams,
  allowedOptions: T
): Array<T[number]['value']> => {
  type V = T[number]['value'];
  const allowed = new Set<string>(allowedOptions.map((o) => o.value));

  // Collect from both repeated params and CSV
  const repeated = searchParams.getAll(paramName);
  const csv = (searchParams.get(paramName) ?? '').split(',');

  const candidates = [...repeated, ...csv].map((s) => s.trim()).filter(Boolean);

  // Dedupe + validate against allow-list
  const out: V[] = [];
  const seen = new Set<string>();
  for (const v of candidates) {
    if (allowed.has(v) && !seen.has(v)) {
      seen.add(v);
      out.push(v as V);
    }
  }
  return out;
};

const getOrderedErrors = (errors: any) => {
  if (!errors) return errors;

  const orderedErrors: Record<string, any> = {};

  // Add errors in the specified order
  FORM_FIELD_ORDER.forEach((field) => {
    if (errors[field]) {
      orderedErrors[field] = errors[field];
    }
  });

  // Add any remaining errors that weren't in our order array
  Object.keys(errors).forEach((field) => {
    if (!(field in orderedErrors)) {
      orderedErrors[field] = errors[field];
    }
  });

  return orderedErrors;
};

export const GetReleaseUpdates: FC<GetReleaseUpdatesProps> = ({
  renderPreSubmit,
  successUrl,
}) => {
  const errorAlertsRef = useRef<ErrorAlertsRef>(null);
  const router = useRouter();
  const searchParams = useSearchParams();

  const initialProductsAndServices = useMemo(
    () => getInitialParamValues('products', searchParams, PRODUCTS_SERVICES),
    [searchParams]
  );

  const initialBrands = useMemo(
    () => getInitialParamValues('brands', searchParams, BRANDS),
    [searchParams]
  );

  const {
    register,
    handleSubmit,
    setFocus,
    reset,
    formState: { errors, isSubmitting, isSubmitted, isValid },
  } = useForm<GetReleaseUpdatesForm>({
    resolver: zodResolver(getReleaseUpdatesSchema),
    defaultValues: {
      productsAndServices: initialProductsAndServices,
      brands: initialBrands,
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const onSubmit = async (data: GetReleaseUpdatesForm) => {
    try {
      await formSubmit(data);
      reset();
      router.push(successUrl);
    } catch (e) {
      console.error('Get Release Updates Submission Error:', e);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box border={false} gap='lg' padding='xs'>
        {!isValid && isSubmitted && errors && (
          <Box margin='lg 0' border={false} data-testid='error-box'>
            <ErrorAlert
              ref={errorAlertsRef}
              errors={getOrderedErrors(errors)}
              setFocus={setFocus}
            />
          </Box>
        )}

        <Text size='T500' weight='bold' as='h2'>
          About you
        </Text>

        <FormInput
          label='First name*'
          id='firstName'
          {...register('firstName')}
          error={errors?.firstName}
          disabled={isSubmitting}
        />

        <FormInput
          label='Last name*'
          id='lastName'
          {...register('lastName')}
          error={errors?.lastName}
          disabled={isSubmitting}
        />

        <Text>
          To receive updates by email or SMS (or both), enter your email and
          mobile number below:
        </Text>

        <FormInput
          label='Email address*'
          id='emailAddr'
          placeholder='me@o2.com'
          error={errors?.emailAddr}
          disabled={isSubmitting}
          {...register('emailAddr')}
        />

        <FormInput
          label='Mobile number*'
          id='contactNo'
          maxLength={11}
          error={errors?.contactNo}
          disabled={isSubmitting}
          {...register('contactNo')}
        />

        <FormRadioGroup id='customerType' error={errors?.customerType?.message}>
          <VerticalRhythm spaceBetween='md'>
            {CUSTOMER_TYPES.map((customerType) => (
              <FormRadioInput
                isFlag={true}
                key={customerType.value}
                value={customerType.value}
                label={customerType.label}
                disabled={isSubmitting}
                type='radio'
                {...register('customerType')}
              />
            ))}
          </VerticalRhythm>
        </FormRadioGroup>

        <FormCheckboxGroup
          id='brands'
          error={errors?.brands?.message}
          label="Pick the brands you'd like to hear about"
        >
          <VerticalRhythm spaceBetween='md'>
            {BRANDS.map((brand) => (
              <FormCheckboxInput
                isSquare={true}
                isFlag={true}
                key={brand.value}
                value={brand.value}
                label={brand.label}
                disabled={isSubmitting}
                type='checkbox'
                {...register('brands')}
              />
            ))}
          </VerticalRhythm>
        </FormCheckboxGroup>

        <FormCheckboxGroup
          id='productsAndServices'
          error={errors?.productsAndServices?.message}
          label="Choose which products and services you'd like to hear about"
        >
          <VerticalRhythm spaceBetween='md'>
            {PRODUCTS_SERVICES.map((brand) => (
              <FormCheckboxInput
                isSquare={true}
                isFlag={true}
                key={brand.value}
                value={brand.value}
                label={brand.label}
                disabled={isSubmitting}
                type='checkbox'
                {...register('productsAndServices')}
              />
            ))}
          </VerticalRhythm>
        </FormCheckboxGroup>

        {renderPreSubmit}

        <Button disabled={isSubmitting} type='submit'>
          Submit
        </Button>
      </Box>
    </form>
  );
};
